import React, { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { getContactDisplayName } from '../../lib/leadHelpers'
import type { CustomerContact } from '../customer/types'

interface PropertyContactsFieldsProps {
  customerContacts: CustomerContact[]
  selectedContactIds: Set<string>
  onSelectedContactIdsChange: (ids: Set<string>) => void
  onAddContact?: (contact: CustomerContact) => void
  /** When true, contacts come from in-memory conversion data (no customer saved yet) */
  conversionMode?: boolean
  compact?: boolean
}

export function PropertyContactsFields({
  customerContacts,
  selectedContactIds,
  onSelectedContactIdsChange,
  onAddContact,
  conversionMode,
  compact,
}: PropertyContactsFieldsProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone: '',
    cell: '',
    email: '',
  })

  const toggleContactSelection = (contactId: string) => {
    const next = new Set(selectedContactIds)
    if (next.has(contactId)) next.delete(contactId)
    else next.add(contactId)
    onSelectedContactIdsChange(next)
  }

  const handleAddContact = () => {
    if (!newContact.first_name.trim() && !newContact.last_name.trim()) return
    const contact: CustomerContact = {
      id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      first_name: newContact.first_name.trim(),
      last_name: newContact.last_name.trim(),
      title: newContact.title.trim(),
      phone: newContact.phone.trim(),
      cell: newContact.cell.trim(),
      email: newContact.email.trim(),
    }
    onAddContact?.(contact)
    const next = new Set(selectedContactIds)
    next.add(contact.id)
    onSelectedContactIdsChange(next)
    setNewContact({ first_name: '', last_name: '', title: '', phone: '', cell: '', email: '' })
    setShowCreateModal(false)
  }

  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  if (!conversionMode && customerContacts.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">
          Please select a customer first to view and select contacts.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          Select contacts from the parent company that are specific to this property:
        </p>
        {onAddContact && (
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Add New Contact
          </button>
        )}
      </div>
      {customerContacts.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600">No contacts yet. Add contacts on the customer step or here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {customerContacts.map((contact) => (
            <label
              key={contact.id}
              className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedContactIds.has(contact.id)}
                onChange={() => toggleContactSelection(contact.id)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{getContactDisplayName(contact)}</div>
                {contact.title && <div className="text-sm text-gray-600">{contact.title}</div>}
                <div className="text-sm text-gray-500 mt-1">
                  {contact.phone && <span>Phone: {contact.phone}</span>}
                  {contact.email && <span className="ml-3">Email: {contact.email}</span>}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add New Contact</h3>
            <p className="text-sm text-gray-600 mb-4">
              This contact will be added to the customer and selected for this property.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={newContact.first_name}
                    onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={newContact.last_name}
                    onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={newContact.title}
                  onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cell</label>
                <input
                  type="tel"
                  value={newContact.cell}
                  onChange={(e) => setNewContact({ ...newContact, cell: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddContact}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
